import { useAccess } from "@/auth/AccessProvider";
import { AuthPageShell } from "@/auth/AuthPageShell";
import { useAuth } from "@/auth/AuthProvider";
import { defaultRouteForLevel, routeKeyFromPathname } from "@/auth/permissions";
import { Button, InlineError, Input, Spinner } from "@/components/ui";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";

function AuthenticatedRedirect({ requestedPath }: { requestedPath: string }) {
  const { loading, defaultRoute, canAccess } = useAccess();

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--color-surface-muted)]">
        <Spinner label="Signing in…" />
      </div>
    );
  }

  const routeKey = routeKeyFromPathname(requestedPath);
  const destination = routeKey && canAccess(routeKey) ? requestedPath : defaultRoute;
  return <Navigate to={destination} replace />;
}

export function LoginPage() {
  const { signIn, session, recoveryMode } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const redirectTo =
    (location.state as { from?: string } | null)?.from &&
    (location.state as { from?: string }).from !== "/login"
      ? (location.state as { from: string }).from
      : defaultRouteForLevel("normal");

  if (session && recoveryMode) {
    return <Navigate to="/reset-password" replace />;
  }

  if (session) {
    return <AuthenticatedRedirect requestedPath={redirectTo} />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const result = await signIn(email.trim(), password);
    if (result.error) {
      setError(result.error);
      setSubmitting(false);
    }
  };

  return (
    <AuthPageShell>
      <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-[var(--color-text)]">Email</span>
          <Input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>

        <label className="block space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-[var(--color-text)]">Password</span>
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-[var(--color-accent)] hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>

        {error ? <InlineError message={error} /> : null}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-[var(--color-text-muted)]">
        <Link to="/signup" className="font-medium text-[var(--color-accent)] hover:underline">
          Create account
        </Link>
      </p>
    </AuthPageShell>
  );
}
